import { Assets, Sprite, Texture } from 'pixi.js';
import { TILE } from './world';

/**
 * The built campfire: an 8-frame animated prop that stands where the player built
 * it (near spawn). It is placed in the y-sorted entity layer so the hero walks
 * behind/in front of it correctly, and animated through the same anim-water list
 * the ticker already drives. Its tile feeds the mob lit-zones — its light keeps
 * night spawns away, matching the tent.
 */

const FRAME_COUNT = 8;
const FPS = 8;
const PX = TILE / 16;   // pack art is 16px-native, like every other map sprite

let framesPromise: Promise<Texture[]> | null = null;

/** Load the campfire animation frames once, shared across placements. */
export function loadCampfireFrames(): Promise<Texture[]> {
  if (!framesPromise) {
    const urls = Array.from({ length: FRAME_COUNT }, (_, i) => `/sprites/props/campfire_${i}.png`);
    framesPromise = Assets.load(urls).then(
      (loaded: Record<string, Texture>) => urls.map((u) => loaded[u]).filter(Boolean),
    );
  }
  return framesPromise;
}

export interface CampfireHandle {
  sprite: Sprite;
  tx: number;
  ty: number;
  /** For the anim-water ticker: swap frames at FPS. */
  anim: { sprite: Sprite; frames: Texture[]; fps: number; t: number; i: number };
}

/** Build a campfire sprite at a tile, anchored at its base for y-sorting. */
export async function createCampfire(tx: number, ty: number): Promise<CampfireHandle> {
  const frames = await loadCampfireFrames();
  const s = new Sprite(frames[0] ?? Texture.WHITE);
  s.anchor.set(0.5, 0.9);
  s.scale.set(PX);
  s.x = (tx + 0.5) * TILE;
  s.y = (ty + 0.9) * TILE;
  s.zIndex = s.y;
  return { sprite: s, tx, ty, anim: { sprite: s, frames, fps: FPS, t: 0, i: 0 } };
}
